// web/main.ts — application entry. Owns the screen flow (menu ↔ game), the
// fixed-timestep render loop, canvas sizing, autosave, and all UI wiring. The
// simulation itself is untouched engine code; this just drives and reflects it.

import { GameSession, DEFAULT_PARAMS, PLAYER_COLONY } from './game.ts';
import type { WorldParams } from './game.ts';
import type { Caste } from '../src/types.ts';
import { validCastesForType, casteCompatLabel, primaryCasteForType, CASTE_NAME as CASTE_LABEL } from '../src/sim/casteGenes.ts';
import { GENE_TYPE_NAME } from '../src/sim/geneData.ts';
import { SPEED_TIERS } from '../src/sim/colonyExample.ts';
import {
  loadSettings, writeSettings, loadSave, writeSave, hasSave, clearSave,
  SAVE_VERSION,
} from './save.ts';
import type { Settings, SaveState } from './save.ts';
import { render } from './render.ts';
import { LogisticsView } from './logistics.ts';
import {
  byId, showScreen, showToast, setLoading, updateAll, updateGenes, rewardCardHTML, rarityRevealClass,
  toggleFuse, fuseSerials, clearFuse, renderTree, renderHistory, setHistoryFilter,
  pushNotification, pushDiscovery, updateWatchOverlay,
  renderEncyclopedia, updateLogistics,
} from './ui.ts';
import type { OpenResult, RewardGene } from './evolutionBox.ts';
import { tierEffect, emergenceOrder, playBurstFx, discoveries, headlineGene } from './boxReveal.ts';
import { audio, tierFanfare } from './audio.ts';
import { RARITY } from '../src/types.ts';
import { ColonyHistory } from './history.ts';
import { WatchView, DEFAULT_VIEW_TILES, pinchZoomFactor } from './watch.ts';
import { ExplorationMap } from './fog.ts';
import { newlyDiscoveredLandmarks } from './landmarks.ts';
import { landmarkName } from '../src/sim/landmarks.ts';
import {
  loadProfile, foldCurrent, recordBoxOpened, recordFusion, recordSacrifice, startColony, dropCurrent,
} from './profile.ts';
import type { PlayerProfile } from './profile.ts';
import { MenuBackground } from './menuBackground.ts';
import {
  loadRecords, saveRecords, mergeRecords, checkRecords, zeroRecords, effectiveSnapshot,
} from './colonyPower.ts';
import {
  snapshotFromSession, geneTypesOf, morphsOf,
  continueCardHTML, newColonyPreviewHTML, archivesHTML, evolutionHTML, statisticsHTML,
} from './menu.ts';
import {
  loadAutoRules, saveAutoRules, decide, summarize, sacrificeYield, DISPOSITION,
  resolveCardHTML, autoSummaryHTML, autoRulesFormHTML,
} from './autoEvolution.ts';
import type { AutoRules, RewardInfo, ResolveCard, RuleSummary } from './autoEvolution.ts';
import { CASTE_NAME } from './game.ts';
import { buildArmory, armoryHTML } from './armory.ts';
import type { AppliedGene } from './armory.ts';
import {
  buildInventory, inventoryBodyHTML, inventoryGridHTML, INV_SORT, INV_FILTER, INV_CATEGORY_ALL,
} from './geneInventory.ts';
import type { InvSort, InvFilter, InventoryOpts } from './geneInventory.ts';
import {
  loadWishlist, writeWishlist, toggleTarget, addTarget, removeTarget, parseTargetKey,
  targetKeySet, wishlistProgress, wishlistSectionHTML, wishlistStripHTML, wishesFulfilledBy, wishName,
  TARGET_CAP,
} from './wishlist.ts';
import type { WishlistState } from './wishlist.ts';

declare const __APP_VERSION__: string;

const MAX_STEPS_PER_FRAME = 400;   // cap so a slow frame / fast-forward can't lock the tab
const HUD_INTERVAL = 0.2;          // seconds between HUD refreshes
const AUTOSAVE_INTERVAL = 30;      // seconds between autosaves

let settings: Settings = loadSettings();
let session: GameSession | null = null;

// Cross-colony meta-progression (Archives / Statistics / Evolution Archive) + the
// living menu backdrop. Presentation/persistence only — never touches the sim.
const profile: PlayerProfile = loadProfile();
const menuBg = new MenuBackground();
/** Seed previewed/used for the NEXT new colony (a blank Settings seed defers to this). */
let pendingSeed = Math.floor(Math.random() * 2 ** 31) >>> 0;
/** The current colony's display name (set on New, restored on Continue). */
let colonyName = '';

let canvas: HTMLCanvasElement;
let ctx: CanvasRenderingContext2D;
let cssW = 800;
let cssH = 600;

let rafId = 0;
let paused = false;
let currentOverlay: string | null = 'menu';
let lastTime = 0;
let acc = 0;
let hudClock = 0;
let autosaveClock = 0;

// Auto-Evolution rules (a UI preference — never in the deterministic save) + the
// queue of rewards left for the manual EQUIP/SACRIFICE/SAVE card after auto-resolve.
let autoRules: AutoRules = loadAutoRules();
let pendingResolve: ResolveCard[] = [];
let lastAutoSummary: RuleSummary | null = null;

// Gene Inventory screen UI state (a render-only view preference — never in the save).
let invCaste = INV_CATEGORY_ALL;
let invSort: InvSort = INV_SORT.Power;
let invQuery = '';
let invFilters: InvFilter[] = [];

// Evolution Wishlist — desired (type, rarity) genes the player is chasing. Persistent
// cross-colony player preference (its own localStorage island); never sim/save state.
const wishlist: WishlistState = loadWishlist();

let boxTimers: number[] = [];
let boxResult: OpenResult | null = null;
/** Guards the discovery feed so a skip can't double-fire the notifications. */
let boxDiscoveriesFired = false;
/** Whether the current open is this session's first sight of its tier (NEW flourish). */
let boxFirstSeen = false;
/** Tiers ever revealed (persisted, render-only) — drives the one-time "NEW" discovery
 *  flourish so a tier's first sight stays spent across reloads. Lives in its OWN
 *  localStorage key, outside the deterministic save, so it never affects replay/hash. */
const SEEN_TIERS_KEY = 'brood.seentiers.v1';
function loadSeenTiers(): Set<number> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(SEEN_TIERS_KEY) : null;
    if (raw === null || raw === undefined) return new Set();
    const arr: unknown = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((n): n is number => typeof n === 'number')) : new Set();
  } catch { return new Set(); }
}
function saveSeenTiers(s: Set<number>): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(SEEN_TIERS_KEY, JSON.stringify([...s]));
  } catch { /* quota / disabled — ignore; the flourish simply re-shows */ }
}
const seenTiers = loadSeenTiers();

let logistics: LogisticsView | null = null;
let history: ColonyHistory | null = null;
let watch: WatchView | null = null;
let fog: ExplorationMap | null = null;
/** Landmark indices the player has already SEEN (fog-revealed) this run — drives the
 *  one-shot "Discovered the …" toast. Per-world (indices are seed-specific), so it's
 *  reset + silently seeded from the starting reveal in startGame; render-only, never saved. */
const seenLandmarks = new Set<number>();
/**
 * Player-extinction game-over state. `gameOverShown` LATCHES so the overlay fires
 * exactly once when the player colony first goes extinct (reset on New Colony).
 * `peakRank`/`peakSize` track the best standing + largest population reached while
 * the colony lived, for the final-standing summary. All pure UI — no sim state.
 */
let gameOverShown = false;
let peakRank = Infinity;
let peakSize = 0;
/** Saved fog blob handed from a load into startGame (cleared once consumed). */
let loadedExplored: import('./fog.ts').ExplorationSave | undefined;
let lastEventCount = 0;
let watchOverlayOn = false;

// Pointer-drag panning state (CSS-pixel anchors; converted to tile deltas via the
// last viewbox so a drag moves the world 1:1 under the cursor).
let dragging = false;
let dragMoved = false;
let dragLastX = 0;
let dragLastY = 0;
let dragPointerId = -1;
/** Pointers currently down on the canvas (Pointer Events) — drives two-finger pinch-zoom. */
const activePointers = new Map<number, { x: number; y: number }>();
/** Finger separation at the last pinch-move; > 0 only while a 2-finger pinch is active. */
let pinchDist = 0;

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

function boot(): void {
  canvas = byId<HTMLCanvasElement>('view');
  const c = canvas.getContext('2d');
  if (!c) { showToast('Canvas not supported in this browser.'); return; }
  ctx = c;

  try { byId('menu-version').textContent = __APP_VERSION__; } catch { /* define injected at build */ }

  applySettingsToInputs();
  wire();
  wireCamera();
  window.addEventListener('resize', () => { if (session) resize(); });
  window.addEventListener('pagehide', () => { if (session && settings.autosave) doSave(true); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && session && settings.autosave) doSave(true);
  });
  document.addEventListener('keydown', (e) => {
    if (!session || currentOverlay !== null) return;
    // Don't hijack keys while typing in a field (seed/settings inputs).
    if (isTypingTarget(e.target)) return;
    if (e.code === 'Space') { e.preventDefault(); togglePause(); }
    else if (e.code === 'KeyC') { watch?.cycleMode(); updateWatchControls(); }
    else if (e.code === 'KeyV') { watch?.cycleObserved(); updateWatchControls(); }
    else if (e.code === 'KeyB') { toggleWatchOverlay(); }
    // Quick-dock hotkeys. Armory is G, not A — A already pans the camera left (handleCameraKey).
    else if (e.code === 'KeyI') { openInventory(); }
    else if (e.code === 'KeyG') { openArmory(); }
    else if (e.code === 'KeyT') { if (session) { renderTree(session); showOverlay('tree'); } }
    else if (handleCameraKey(e)) { /* pan/zoom/recenter consumed it */ }
  });

  refreshMenu();
  showScreen('menu');
  menuBg.start(byId<HTMLCanvasElement>('menu-bg')); // begin the living backdrop
  rafId = requestAnimationFrame(frame);
}

// ---------------------------------------------------------------------------
// Screen flow
// ---------------------------------------------------------------------------

const OVERLAY_SCREENS = [
  'menu', 'settings', 'howto', 'archives', 'evolution', 'stats',
  'onboard', 'gameover', 'box', 'tree', 'history', 'encyclopedia', 'autorules', 'armory', 'inventory',
];
/** Pre-game menu overlays (the living backdrop runs behind exactly these). */
const MENU_FAMILY = new Set(['menu', 'settings', 'howto', 'archives', 'evolution', 'stats']);

function showOverlay(name: string | null): void {
  currentOverlay = name;
  for (const o of OVERLAY_SCREENS) byId(`screen-${o}`).hidden = o !== name;
  if (name === null) byId('screen-game').hidden = false;
  // Animate the living colony backdrop only behind the menu family — in-game it is
  // hidden behind the game canvas, so there's no point spending frames on it.
  if (name !== null && MENU_FAMILY.has(name)) menuBg.start(byId<HTMLCanvasElement>('menu-bg'));
  else menuBg.stop();
}

/** Seed for the next New Colony: an explicit Settings seed wins; else the rolled one. */
function currentSeed(): number {
  const raw = byId<HTMLInputElement>('set-seed').value.trim();
  if (raw !== '') { const v = Number.parseInt(raw, 10); if (Number.isFinite(v)) return v >>> 0; }
  return pendingSeed;
}

function refreshMenu(): void {
  const hasColony = !!(session || hasSave());
  // CONTINUE card — prefer a LIVE read when a session is in progress (exact rank / pop / age
  // right now), else the snapshot folded at the last save (for a saved-but-not-running colony).
  const card = byId('mm-continue-card');
  const liveSnap = session
    ? snapshotFromSession(session, history, colonyName || profile.current?.name || 'Colony',
        session.params.seed, profile.current?.createdAt ?? Date.now())
    : null;
  const body = continueCardHTML(liveSnap ?? profile.current ?? null, liveSnap != null);
  if (hasColony && body) { card.hidden = false; byId('mm-continue').innerHTML = body; }
  else card.hidden = true;
  byId<HTMLButtonElement>('btn-continue').disabled = !hasColony;
  // NEW COLONY — seed + starting-region preview.
  byId('mm-new-preview').innerHTML = newColonyPreviewHTML(currentSeed());
  const save = loadSave();
  byId('menu-savemeta').textContent = save
    ? `Saved colony — tick ${save.tick}, seed ${save.params.seed}.`
    : (session ? 'Unsaved colony in progress.' : 'No saved colony yet.');
}

function newColony(): void {
  if ((session || hasSave()) &&
      !window.confirm('Start a new colony? This replaces your current saved colony.')) return;
  const seed = currentSeed();
  colonyName = byId<HTMLInputElement>('set-name').value.trim() || `Colony ${profile.coloniesCreated + 1}`;
  // Archive the colony being replaced + bump the lifetime "colonies created" counter.
  startColony(profile);
  pendingSeed = Math.floor(Math.random() * 2 ** 31) >>> 0; // fresh seed for the next one
  const params: WorldParams = {
    ...DEFAULT_PARAMS,
    seed,
    width: clampInt(settings.width, 24, 96, DEFAULT_PARAMS.width),
    height: clampInt(settings.height, 16, 64, DEFAULT_PARAMS.height),
    colonies: clampInt(settings.colonies, 2, 6, DEFAULT_PARAMS.colonies),
  };
  session = new GameSession(params);
  history = new ColonyHistory();
  startGame(true);
}

function continueColony(): void {
  if (session) { showOverlay(null); resyncTiming(); return; } // resume in-memory game
  const save = loadSave();
  if (!save) { showToast('No saved colony found.'); return; }
  colonyName = save.name || 'Colony';
  setLoading(true);
  // Yield once so the loading overlay paints before the synchronous replay.
  setTimeout(() => {
    try {
      session = GameSession.fromSave(save.params, save.actions, save.tick);
      history = ColonyHistory.fromJSON(save.history ?? []);
      loadedExplored = save.explored; // restored in startGame (dims-mismatch → fresh)
      if (save.hash && session.hash() !== save.hash) {
        showToast('Loaded — integrity hash differed (continuing anyway).', 3200);
      }
      startGame(false);
    } catch (err) {
      console.error(err);
      showToast('Failed to load saved colony.');
    } finally {
      setLoading(false);
    }
  }, 30);
}

function startGame(isNew: boolean): void {
  byId('screen-game').hidden = false;
  paused = false;
  // Reset the game-over latch + peak trackers for a fresh colony (also covers the
  // game-over "New Colony" path). On load, clear them too — a loaded colony that is
  // already extinct will re-latch on the next HUD refresh via checkGameOver.
  gameOverShown = false;
  peakRank = Infinity;
  peakSize = 0;
  const s = session as GameSession;
  const gw = s.world.grid.width;
  const gh = s.world.grid.height;
  logistics = new LogisticsView(gw, gh, s.world.colonies.capacity);
  watch = new WatchView(s.world.entities.capacity);
  // Fog of war: fresh on a new colony; restored from the save (then revealed once
  // around current units) on load. A dims-mismatch save degrades to a fresh map.
  fog = isNew ? new ExplorationMap(gw, gh) : ExplorationMap.fromSave(loadedExplored, gw, gh);
  loadedExplored = undefined;
  lastEventCount = history ? history.count() : 0;
  updateWatchControls();
  resize(); // sets cssW/cssH so the opening zoom (~32 tiles across) is correct
  // Open the camera ON THE PLAYER NEST, zoomed in — the player must NOT see the
  // whole world at start; they discover it by panning out and scouting.
  const nest = s.playerNest();
  const zoom = watch.zoomForTilesAcross(DEFAULT_VIEW_TILES, cssW, cssH, gw, gh);
  watch.initManual(nest.x + 0.5, nest.y + 0.5, zoom);
  fog.reveal(s); // light up the spawn area immediately (esp. on load)
  // Silently baseline the discovered-landmark set to whatever the opening reveal already
  // shows (the spawn area, or a loaded save's explored map), so we don't spam "Discovered"
  // toasts for places the player already knows — only genuinely NEW reveals in play notify.
  seenLandmarks.clear();
  for (const i of newlyDiscoveredLandmarks(s.world.landmarks, fog, seenLandmarks)) seenLandmarks.add(i);
  updateAll(session as GameSession);
  showOverlay(isNew ? 'onboard' : null);
  resyncTiming();
  if (isNew) doSave(true); // make "Continue" immediately available
}

function resyncTiming(): void {
  lastTime = 0; acc = 0; hudClock = HUD_INTERVAL; autosaveClock = 0;
}

function openMenu(): void {
  if (session) doSave(true);
  refreshMenu();
  showOverlay('menu');
}

function togglePause(): void {
  paused = !paused;
  byId('btn-pause').textContent = paused ? '▶ Resume' : '⏸ Pause';
}

// ---------------------------------------------------------------------------
// Render / sim loop
// ---------------------------------------------------------------------------

function frame(now: number): void {
  rafId = requestAnimationFrame(frame);
  if (!session) return;

  const dt = lastTime ? Math.min((now - lastTime) / 1000, 0.25) : 0;
  lastTime = now;

  const simActive = !paused && currentOverlay === null && byId('loading').hidden;
  if (simActive) {
    acc += dt * settings.ticksPerSecond * settings.speedMultiplier;
    let steps = Math.floor(acc);
    if (steps > 0) {
      acc -= steps;
      if (steps > MAX_STEPS_PER_FRAME) { steps = MAX_STEPS_PER_FRAME; acc = 0; }
      session.step(steps);
    }
    autosaveClock += dt;
    if (settings.autosave && autosaveClock >= AUTOSAVE_INTERVAL) { autosaveClock = 0; doSave(true); }
    logistics?.update(session, dt); // advance trails/couriers with the live sim
  }

  if (!byId('screen-game').hidden) {
    watch?.update(session, dt, session.world.grid.width, session.world.grid.height);
    fog?.reveal(session); // refresh discovered/visible from the live player units
    // First-sight landmark discovery: a landmark whose centre just entered the fog pops a
    // one-shot "Discovered the …" toast. Render-only — NOT a chronicle/save event.
    const lm = session.world.landmarks;
    if (lm) for (const i of newlyDiscoveredLandmarks(lm, fog, seenLandmarks)) {
      seenLandmarks.add(i);
      pushNotification({ tick: session.tick, type: 'region', title: `Discovered the ${landmarkName(lm.typeAt(i))}`, detail: '' });
    }
    render(ctx, cssW, cssH, session, settings, logistics, watch, fog);
  }

  hudClock += dt;
  if (hudClock >= HUD_INTERVAL) {
    hudClock = 0;
    session.samplePopulation();
    history?.observe(session);
    flushNotifications();
    checkGameOver();
    updateAll(session);
    // Ambient colony bed: a thriving hive sounds busier. Intensity from live
    // population (dominant) + a small activity bump (hives/military), normalized so a
    // mid-size colony approaches full. Render-only; mute/volume govern it in audio.ts.
    { const pi = session.playerInfo(); audio.setAmbient((pi.population + pi.hives * 2 + pi.military * 0.5) / 30); }
    updateLogistics(session, logistics);
    if (fog) byId('cam-explored').textContent = `${fog.exploredPct()}%`;
    if (watchOverlayOn) updateWatchOverlay(session, watch);
    // Keep the encyclopedia's live counts fresh while it's open.
    if (currentOverlay === 'encyclopedia') renderEncyclopedia(session);
  }
}

/**
 * Track the player's best standing while alive, and — the first time the player
 * colony goes extinct — latch + raise the game-over overlay (pausing the sim like
 * any other overlay). Pure UI/flow: it reads sim state but never mutates it. The
 * latch resets only on New Colony, so the prompt fires exactly once per colony.
 */
function checkGameOver(): void {
  if (!session) return;
  if (!gameOverShown) {
    // While alive, remember the best (lowest) rank and the largest population.
    const pop = session.colonyPopulation(PLAYER_COLONY);
    if (pop > peakSize) peakSize = pop;
    const rank = session.playerRank().rank;
    if (rank >= 1 && rank < peakRank) peakRank = rank;
  }
  if (!gameOverShown && session.playerExtinct()) {
    gameOverShown = true;
    showGameOver();
  }
}

/** Populate + raise the game-over overlay with the player's final standing. */
function showGameOver(): void {
  if (!session) return;
  const { of } = session.playerRank();
  const rankStr = peakRank === Infinity ? '—' : `#${peakRank} of ${of}`;
  const stats: [string, string][] = [
    ['Ticks survived', String(session.tick)],
    ['Peak rank', rankStr],
    ['Peak colony size', String(peakSize)],
  ];
  byId('gameover-stats').innerHTML = stats
    .map(([k, v]) => `<li><span class="muted">${k}</span><span>${v}</span></li>`)
    .join('');
  audio.play('click');
  showOverlay('gameover');
}

/** Surface freshly-observed history events as on-screen notifications. */
function flushNotifications(): void {
  if (!history) return;
  const c = history.count();
  if (c > lastEventCount) {
    for (const ev of history.list().slice(lastEventCount, c).slice(-3)) pushNotification(ev);
    lastEventCount = c;
  }
}

function updateWatchControls(): void {
  if (!watch) return;
  byId('btn-camera').textContent = `🎥 ${watch.modeLabel()}`;
  const names = ['Worker', 'Soldier', 'Flyer', 'Specialist'];
  byId('btn-observe').textContent = `👁 ${watch.observedCaste < 0 ? 'Off' : (names[watch.observedCaste] as string)}`;
  if (fog) byId('cam-explored').textContent = `${fog.exploredPct()}%`;
}

function toggleWatchOverlay(): void {
  watchOverlayOn = !watchOverlayOn;
  byId('watch-overlay').hidden = !watchOverlayOn;
  byId('btn-watch').classList.toggle('active', watchOverlayOn);
  if (watchOverlayOn && session) updateWatchOverlay(session, watch);
}

function resize(): void {
  const rect = byId('view-wrap').getBoundingClientRect();
  cssW = Math.max(120, Math.floor(rect.width));
  cssH = Math.max(120, Math.floor(rect.height));
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(cssW * dpr);
  canvas.height = Math.floor(cssH * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

// ---------------------------------------------------------------------------
// Camera input (pan / zoom) — drives the manual player camera in WatchView
// ---------------------------------------------------------------------------

/** True if the event target is a text-entry control we shouldn't steal keys from. */
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

const KEY_PAN_TILES = 4;   // tiles per keypress pan step
const KEY_ZOOM_STEP = 1.2; // multiplicative zoom per +/- press
const BTN_ZOOM_STEP = 1.25;

/** WASD / arrows pan; +/- (= and -) zoom; Home / R recenter on the nest. Returns handled. */
function handleCameraKey(e: KeyboardEvent): boolean {
  if (!watch || !session) return false;
  switch (e.code) {
    case 'ArrowLeft': case 'KeyA': watch.panBy(-KEY_PAN_TILES, 0); return true;
    case 'ArrowRight': case 'KeyD': watch.panBy(KEY_PAN_TILES, 0); return true;
    case 'ArrowUp': case 'KeyW': watch.panBy(0, -KEY_PAN_TILES); return true;
    case 'ArrowDown': case 'KeyS': watch.panBy(0, KEY_PAN_TILES); return true;
    case 'Equal': case 'NumpadAdd': zoomCenter(KEY_ZOOM_STEP); return true;
    case 'Minus': case 'NumpadSubtract': zoomCenter(1 / KEY_ZOOM_STEP); return true;
    case 'Home': case 'KeyR': recenterOnNest(); return true;
    default: return false;
  }
}

/** Zoom about the canvas center by `factor` (used by keys + the +/− buttons). */
function zoomCenter(factor: number): void {
  if (!watch || !session) return;
  const gw = session.world.grid.width;
  const gh = session.world.grid.height;
  watch.zoomBy(factor, cssW / 2, cssH / 2, cssW, cssH, gw, gh);
  updateWatchControls();
}

/** Snap the camera back to the player nest (keeps current zoom). */
function recenterOnNest(): void {
  if (!watch || !session) return;
  const nest = session.playerNest();
  watch.centerOn(nest.x + 0.5, nest.y + 0.5);
  updateWatchControls();
}

/** Attach pointer-drag + wheel-zoom handlers to the canvas. */
function wireCamera(): void {
  // Pointer drag → pan. A small movement threshold keeps clicks from panning.
  canvas.addEventListener('pointerdown', (e) => {
    if (!watch || currentOverlay !== null) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.size >= 2) {
      // A second finger begins a pinch — cancel any single-finger pan and seed the distance.
      dragging = false;
      const pts = [...activePointers.values()];
      pinchDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      return;
    }
    dragging = true; dragMoved = false; dragPointerId = e.pointerId;
    dragLastX = e.clientX; dragLastY = e.clientY;
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!watch || !session) return;
    if (activePointers.has(e.pointerId)) activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const gw = session.world.grid.width;
    const gh = session.world.grid.height;
    // Two-finger pinch → zoom toward the midpoint between the fingers.
    if (activePointers.size >= 2 && currentOverlay === null) {
      const pts = [...activePointers.values()];
      const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      if (pinchDist > 0 && dist > 0) {
        const rect = canvas.getBoundingClientRect();
        const midX = (pts[0]!.x + pts[1]!.x) / 2 - rect.left;
        const midY = (pts[0]!.y + pts[1]!.y) / 2 - rect.top;
        watch.zoomBy(pinchZoomFactor(pinchDist, dist), midX, midY, cssW, cssH, gw, gh);
        updateWatchControls();
      }
      pinchDist = dist;
      return; // a pinch never also pans
    }
    if (!dragging) return;
    const dxPx = e.clientX - dragLastX;
    const dyPx = e.clientY - dragLastY;
    if (!dragMoved && Math.abs(dxPx) + Math.abs(dyPx) < 4) return; // threshold
    dragMoved = true;
    dragLastX = e.clientX; dragLastY = e.clientY;
    // Convert the CSS-pixel drag to a tile delta via the live viewbox, then pan the
    // OPPOSITE way so the grabbed point tracks the cursor.
    const view = watch.viewbox(cssW, cssH, gw, gh);
    watch.panBy(-dxPx / view.cell, -dyPx / view.cell);
    try { canvas.setPointerCapture(dragPointerId); } catch { /* not all browsers */ }
  });
  const endDrag = (e: PointerEvent): void => {
    activePointers.delete(e.pointerId);
    if (activePointers.size < 2) pinchDist = 0; // pinch ends when fewer than two fingers remain
    dragging = false; dragPointerId = -1;
  };
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  // Wheel → zoom toward the cursor.
  canvas.addEventListener('wheel', (e) => {
    if (!watch || !session || currentOverlay !== null) return;
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const ax = e.clientX - rect.left; // CSS px within the canvas
    const ay = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? BTN_ZOOM_STEP : 1 / BTN_ZOOM_STEP;
    const gw = session.world.grid.width;
    const gh = session.world.grid.height;
    watch.zoomBy(factor, ax, ay, cssW, cssH, gw, gh);
    updateWatchControls();
  }, { passive: false });
}

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

function doSave(auto: boolean): void {
  if (!session) return;
  const prev = loadSave();
  const createdAt = prev && prev.params.seed === session.params.seed ? prev.createdAt : Date.now();
  const name = colonyName || (prev ? prev.name : 'Colony');
  const save: SaveState = {
    version: SAVE_VERSION,
    name,
    createdAt,
    savedAt: Date.now(),
    params: session.params,
    tick: session.tick,
    actions: session.actions,
    hash: session.hash(),
    history: history ? history.toJSON() : [],
  };
  // Fog blob (non-replayed). Set only when present so exactOptionalPropertyTypes is
  // satisfied (no explicit `undefined`); legacy saves simply omit it.
  if (fog) save.explored = fog.toSave();
  const ok = writeSave(save);
  // Reflect the live colony in the persistent profile so the menu's Continue card +
  // lifetime bests stay current without ever replaying. Best-effort (never blocks a save).
  try {
    const snap = snapshotFromSession(session, history, name, session.params.seed, createdAt);
    foldCurrent(profile, snap, geneTypesOf(session), morphsOf(session), session.boxState().current);
  } catch { /* presentation-only; ignore */ }
  if (!auto) showToast(ok ? `Saved at tick ${session.tick}.` : 'Save failed — storage unavailable.');
}

// ---------------------------------------------------------------------------
// Evolution box opening (animation + reveal sequence)
// ---------------------------------------------------------------------------

function openBoxFlow(): void {
  if (!session) return;
  // Snapshot the inventory so we can pick out THIS open's new drops. We keep ALL
  // rewards (no auto-dup-convert) so the auto-rules + manual card resolve everything.
  const before = new Set<number>(session.playerGenes().map((g) => g.id));
  const res = session.openBox(true);
  if (!res.ok || !res.result) { showToast(res.message); return; }
  history?.recordBoxOpen(session, res.result.tier, res.result.tierName);
  recordBoxOpened(profile, res.result.sacrificed, res.result.tier, session.boxState().current + res.result.cost);

  // The new drops (oldest serial first → deterministic resolution order).
  const drops = session.playerGenes()
    .filter((g) => !before.has(g.id))
    .map((g) => session!.geneInfo(g.id))
    .filter((gi): gi is NonNullable<typeof gi> => gi !== null)
    .sort((a, b) => a.serial - b.serial);

  // Auto-resolve each drop per the rules; queue the rest for the manual card.
  pendingResolve = [];
  const summary: RuleSummary = { equip: 0, sacrifice: 0, save: 0, ask: 0, energy: 0 };
  for (const gi of drops) {
    const card = buildResolveCard(gi);
    const info: RewardInfo = {
      type: gi.type, rarity: gi.rarity, traitTotal: gi.traitTotal, power: card.power,
      isDuplicate: card.currentPower >= 0,
      isUpgrade: card.currentPower >= 0 ? card.power > card.currentPower : true,
    };
    const d = decide(info, autoRules);
    if (d === DISPOSITION.Equip) { session.applyGene(card.geneId, primaryCasteForType(card.type) as Caste, false); summary.equip++; }
    else if (d === DISPOSITION.Sacrifice) { summary.energy += session.sacrificeGene(card.serial).energy; summary.sacrifice++; }
    else if (d === DISPOSITION.Save) { summary.save++; }
    else { pendingResolve.push(card); summary.ask++; }
  }
  lastAutoSummary = summary;
  if (summary.sacrifice > 0) recordSacrifice(profile, summary.sacrifice); // lifetime stat

  startBoxAnimation(res.result);
  doSave(true); // persist the open + any auto-resolutions immediately
}

/** Build a manual-resolution card for a freshly-dropped gene (power + current best). */
function buildResolveCard(gi: { id: number; serial: number; type: number; rarity: number; traitTotal: number; power: number; label: string }): ResolveCard {
  let bestPower = -1;
  let bestLabel = '';
  for (const pg of session!.playerGenes()) {
    if (pg.id === gi.id) continue;
    const o = session!.geneInfo(pg.id);
    if (!o || o.type !== gi.type) continue;
    if (o.power > bestPower) { bestPower = o.power; bestLabel = o.label; }
  }
  return {
    geneId: gi.id, serial: gi.serial, name: gi.label, type: gi.type, rarity: gi.rarity,
    traitTotal: gi.traitTotal, power: gi.power,
    currentLabel: bestLabel, currentPower: bestPower, sacrificeEnergy: sacrificeYield(gi.rarity),
    equipCasteName: CASTE_NAME[primaryCasteForType(gi.type)] ?? 'caste',
  };
}

/** Show the next pending reward card, or reveal the Collect button when done. */
function renderResolve(): void {
  const card = pendingResolve[0];
  const el = byId('box-resolve');
  if (!card) { el.hidden = true; el.innerHTML = ''; byId('box-footer').hidden = false; return; }
  el.innerHTML = resolveCardHTML(card, pendingResolve.length);
  el.hidden = false;
  byId('box-footer').hidden = true; // Collect appears once every reward is resolved
}

/** Resolve the front pending reward with the chosen disposition, then advance. */
function resolvePending(act: string): void {
  if (!session) return;
  const card = pendingResolve[0];
  if (!card) return;
  if (act === 'equip') session.applyGene(card.geneId, primaryCasteForType(card.type) as Caste, false);
  else if (act === 'sacrifice') { session.sacrificeGene(card.serial); recordSacrifice(profile, 1); }
  // 'save' ⇒ keep it (no action).
  pendingResolve.shift();
  audio.play('click');
  updateAll(session);
  doSave(true);
  renderResolve();
}

/**
 * The cinematic opening sequence — opening a box is an EVENT, staged in distinct beats
 * (mostly CSS keyframes fired by a handful of `boxTimers`):
 *
 *   DARKEN → the screen dims (overlay `.darken`).
 *   ENTER  → the box flies into centre screen (`.enter`).
 *   SURGE  → energy charges into it (`.charging`, the glow winds up).
 *   CRACK  → it shakes then bursts open (`.shake` → `.burst` + particle/beam FX).
 *   REVEAL → the collectible cards emerge least→most rare, finale on the best gene, and
 *            the headline gene's RARITY keys a per-rarity reveal effect on the whole
 *            stage (`rv-common` glow … `rv-apex` screen distortion).
 *
 * The "Skip" path (skipBoxAnimation) still jumps straight to the full reveal.
 */
function startBoxAnimation(result: OpenResult): void {
  clearBoxTimers();
  boxResult = result;
  const eff = tierEffect(result.tier);
  boxFirstSeen = !seenTiers.has(result.tier);
  if (boxFirstSeen) { seenTiers.add(result.tier); saveSeenTiers(seenTiers); }

  boxDiscoveriesFired = false;
  // The per-rarity reveal effect is keyed off the BEST gene of the open (its headline);
  // an all-duplicate open falls back to a gentle common glow.
  const head = headlineGene(result);
  const revealCls = rarityRevealClass(head ? head.rarity : 0);
  const overlay = byId('screen-box');
  // BEAT 1 — DARKEN: tier-* tints the stage; `darken` dims the whole screen as the box arrives.
  overlay.className = `screen overlay box-overlay tier-${result.tier} darken`;

  const big = byId('box-art-big');
  // BEAT 2 — ENTER then BEAT 3 — SURGE: the box flies in, then winds up its charge glow.
  big.className = `box-art-big tier-${result.tier} enter`;
  byId('box-fx').innerHTML = '';
  byId('box-tier-label').textContent = '';
  const banner = byId('box-banner');
  banner.textContent = '';
  banner.className = `box-banner tier-${result.tier}`;
  byId('box-reveal').innerHTML = '';
  byId('box-footer').hidden = true;
  showOverlay('box');
  audio.play('boxCharge');

  // After the entrance, surge energy into the box (the charge-up glow).
  boxTimers.push(timer(() => {
    big.classList.remove('enter');
    big.classList.add('charging');
  }, 360));

  // BEAT 4 — CRACK (anticipation): a glowing charge, then a shake that intensifies by tier.
  boxTimers.push(timer(() => {
    big.classList.remove('charging');
    big.classList.add('shake');
  }, 360 + 180));

  // BEAT 5 — burst the box open, fire particles/beams, drop the named banner, light the
  //          per-rarity reveal effect, and (Apex+) flash + quake the whole stage.
  boxTimers.push(timer(() => {
    big.classList.remove('shake');
    big.classList.add('burst');
    playBurstFx(byId('box-fx'), result.tier);
    audio.play('boxBurst');
    // Per-rarity reveal effect on the stage, keyed off the headline gene's rarity.
    overlay.classList.add(revealCls);
    // The tier fanfare lands just after the burst crack (major drops only).
    const fanfare = tierFanfare(result.tier);
    if (fanfare !== null) boxTimers.push(timer(() => audio.play(fanfare), 140));
    byId('box-tier-label').textContent = `${result.tierName} Box`;
    if (eff.banner) {
      banner.textContent = eff.banner;
      banner.classList.add('show');
    }
    if (eff.flash) overlay.classList.add(eff.flash);
    // Apex headline OR Apex+ tier warps/shakes the whole screen (the distortion beat).
    if (eff.quake || revealCls === 'rv-apex') {
      overlay.classList.add('quake');
      boxTimers.push(timer(() => overlay.classList.remove('quake'), 700));
    }
    // Discovery notifications — outlive the overlay, celebrate the major drops.
    fireDiscoveries(result, boxFirstSeen);

    // BEAT 6 — REVEAL: gene emergence least→most rare, so the best gene is the climax.
    const order = emergenceOrder(result.genes);
    const lead = 460;
    order.forEach((g, i) => boxTimers.push(timer(() => revealReward(g, i === order.length - 1), lead + i * eff.stepMs)));
    boxTimers.push(timer(() => showBoxFooter(result), lead + order.length * eff.stepMs + 260));
  }, 360 + 180 + eff.shakeMs));
}

/** True when a box reward satisfies a gene on the player's Evolution Wishlist. */
function isWishReward(g: RewardGene): boolean {
  return wishesFulfilledBy(wishlist.targets, g.type, g.rarity).length > 0;
}

function revealReward(g: RewardGene, finale: boolean): void {
  const gen = session ? session.playerGeneration() : undefined;
  byId('box-reveal').insertAdjacentHTML(
    'beforeend', rewardCardHTML(g, isWishReward(g), gen).replace('class="reward', `class="reward emerge${finale ? ' finale' : ''}`),
  );
  audio.play(g.rarity >= RARITY.Epic ? 'revealRare' : 'reveal');
}

function showBoxFooter(result: OpenResult): void {
  byId('box-refund').innerHTML = lastAutoSummary
    ? autoSummaryHTML(lastAutoSummary)
    : (result.refunded > 0 ? `+${result.refunded} Mutation Energy from duplicates` : 'Rewards added to your inventory.');
  // Anything the auto-rules didn't resolve gets the prominent manual card (EQUIP /
  // SACRIFICE / SAVE); Collect appears only once every reward is resolved.
  if (pendingResolve.length > 0) renderResolve();
  else { byId('box-resolve').hidden = true; byId('box-footer').hidden = false; }
}

/** Fire the discovery feed once per open (guarded so a skip can't double it). */
function fireDiscoveries(result: OpenResult, firstSeen: boolean): void {
  if (boxDiscoveriesFired) return;
  boxDiscoveriesFired = true;
  for (const d of discoveries(result, firstSeen)) pushDiscovery(d);
  // Celebrate any Evolution Wishlist genes this open delivered (deduped + capped at 2
  // so a lucky multi-drop can't flood the feed).
  const seen = new Set<string>();
  for (const g of result.genes) {
    for (const tgt of wishesFulfilledBy(wishlist.targets, g.type, g.rarity)) {
      const key = `${tgt.type}:${tgt.rarity}`;
      if (seen.has(key)) continue;
      seen.add(key);
      pushDiscovery({ icon: '★', title: `Wishlist gene found: ${wishName(tgt.type, tgt.rarity)}`, cls: 'apex' });
      if (seen.size >= 2) break;
    }
    if (seen.size >= 2) break;
  }
}

function skipBoxAnimation(): void {
  if (!boxResult) return;
  clearBoxTimers();
  const result = boxResult;
  const eff = tierEffect(result.tier);
  // Snap to the post-burst state: drop the entrance/charge phases, settle the box open,
  // and light the per-rarity reveal effect so a skip still lands on the full reveal.
  const head = headlineGene(result);
  byId('screen-box').classList.add(rarityRevealClass(head ? head.rarity : 0));
  byId('box-art-big').classList.remove('enter', 'shake', 'charging');
  byId('box-tier-label').textContent = `${result.tierName} Box`;
  if (eff.banner) {
    const banner = byId('box-banner');
    banner.textContent = eff.banner;
    banner.classList.add('show');
  }
  // Jump straight to the full reveal — emergence order, finale on the best gene.
  const gen = session ? session.playerGeneration() : undefined;
  const order = emergenceOrder(result.genes);
  byId('box-reveal').innerHTML = order
    .map((g, i) => rewardCardHTML(g, isWishReward(g), gen).replace('class="reward', `class="reward${i === order.length - 1 ? ' finale' : ''}`))
    .join('');
  fireDiscoveries(result, boxFirstSeen);
  showBoxFooter(result);
}

function finishBox(): void {
  clearBoxTimers();
  boxResult = null;
  pendingResolve = [];
  lastAutoSummary = null;
  byId('box-resolve').hidden = true;
  byId('box-resolve').innerHTML = '';
  showOverlay(null);
  resyncTiming();
  if (session) { updateAll(session); doSave(true); }
}

function clearBoxTimers(): void {
  for (const t of boxTimers) clearTimeout(t);
  boxTimers = [];
}
function timer(fn: () => void, ms: number): number {
  return setTimeout(fn, ms) as unknown as number;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

function applySettingsToInputs(): void {
  byId<HTMLInputElement>('set-tps').value = String(settings.ticksPerSecond);
  byId('set-tps-val').textContent = String(settings.ticksPerSecond);
  byId<HTMLSelectElement>('set-mult').value = String(settings.speedMultiplier);
  byId<HTMLInputElement>('set-volume').value = String(Math.round(settings.masterVolume * 100));
  byId('set-volume-val').textContent = `${Math.round(settings.masterVolume * 100)}%`;
  byId<HTMLInputElement>('set-muted').checked = settings.muted;
  byId<HTMLInputElement>('set-hive').checked = settings.showHiveNodes;
  byId<HTMLInputElement>('set-grid').checked = settings.showGrid;
  byId<HTMLInputElement>('set-trails').checked = settings.showTrails;
  byId<HTMLInputElement>('set-generations').checked = settings.showGenerations;
  byId<HTMLInputElement>('set-autosave').checked = settings.autosave;
  byId<HTMLInputElement>('set-width').value = String(settings.width);
  byId<HTMLInputElement>('set-height').value = String(settings.height);
  byId<HTMLInputElement>('set-colonies').value = String(settings.colonies);
}

function saveSettingsFromInputs(): void {
  settings = {
    ticksPerSecond: clampInt(Number.parseInt(byId<HTMLInputElement>('set-tps').value, 10), 2, 60, 20),
    // Parse as a float + clamp to a known SPEED_TIERS value so the 0.5× tier persists.
    speedMultiplier: clampSpeed(Number.parseFloat(byId<HTMLSelectElement>('set-mult').value)),
    masterVolume: clampInt(Number.parseInt(byId<HTMLInputElement>('set-volume').value, 10), 0, 100, 50) / 100,
    muted: byId<HTMLInputElement>('set-muted').checked,
    showHiveNodes: byId<HTMLInputElement>('set-hive').checked,
    showGrid: byId<HTMLInputElement>('set-grid').checked,
    showTrails: byId<HTMLInputElement>('set-trails').checked,
    showGenerations: byId<HTMLInputElement>('set-generations').checked,
    autosave: byId<HTMLInputElement>('set-autosave').checked,
    width: clampInt(Number.parseInt(byId<HTMLInputElement>('set-width').value, 10), 24, 96, 56),
    height: clampInt(Number.parseInt(byId<HTMLInputElement>('set-height').value, 10), 16, 64, 32),
    colonies: clampInt(Number.parseInt(byId<HTMLInputElement>('set-colonies').value, 10), 2, 6, 4),
  };
  writeSettings(settings);
  audio.applySettings(settings.masterVolume, settings.muted);
  syncSpeedButtons();
  showToast('Settings saved.');
}

function syncSpeedButtons(): void {
  for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>('.speed'))) {
    b.classList.toggle('active', b.dataset['speed'] === String(settings.speedMultiplier));
  }
}

// ---------------------------------------------------------------------------
// Colony power records (render/persistence-only "NEW COLONY RECORD" banners)
// ---------------------------------------------------------------------------

/**
 * After a gene is applied, fire a celebratory banner for any caste/colony power that
 * just reached a new all-time high. Records live in their OWN localStorage key
 * (web/colonyPower.ts) — wholly separate from the deterministic save — so this is pure
 * render/persistence state and cannot touch the sim, determinism, or replay.
 *
 * First-ever call (no records stored): SEED silently from the current snapshot (merged
 * over a zero baseline) and fire nothing, so the very first apply never spuriously
 * "breaks" a record against an empty store.
 */
function checkColonyRecords(s: GameSession): void {
  const cur = s.powerSnapshot();
  const records = loadRecords();
  if (records === null) {
    saveRecords(mergeRecords(zeroRecords(), cur));
    return;
  }
  const hits = checkRecords(records, cur);
  if (hits.length === 0) return;
  saveRecords(mergeRecords(records, cur));
  // Records are tracked RAW (monotonic, determinism-safe), but the banner shows the value
  // through the same DIMINISHING-returns display curve as the HUD. Each hit's raw value is
  // exactly cur[hit.key], so effectiveSnapshot(cur)[hit.key] is its displayed form (and the
  // colony hit reads as the SUM of effective castes — consistent with the HUD pill).
  const eff = effectiveSnapshot(cur);
  for (const hit of hits) {
    pushDiscovery({
      icon: '🏆',
      title: `NEW COLONY RECORD · ${hit.label}: ${eff[hit.key].toLocaleString('en-US')}`,
      cls: 'record',
    });
  }
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

function wire(): void {
  byId('btn-new').addEventListener('click', newColony);
  byId('btn-continue').addEventListener('click', continueColony);
  byId('btn-settings').addEventListener('click', () => { applySettingsToInputs(); showOverlay('settings'); });
  byId('btn-howto').addEventListener('click', () => showOverlay('howto'));
  byId('btn-howto-back').addEventListener('click', () => showOverlay('menu'));

  // Menu 2.0 sub-screens (rendered fresh from the persistent profile on open).
  byId('btn-archives').addEventListener('click', () => { byId('archives-body').innerHTML = archivesHTML(profile); showOverlay('archives'); });
  byId('btn-evolution').addEventListener('click', () => { byId('evolution-body').innerHTML = evolutionHTML(profile, session); showOverlay('evolution'); });
  byId('btn-stats').addEventListener('click', () => { byId('stats-body').innerHTML = statisticsHTML(profile, loadRecords()); showOverlay('stats'); });
  byId('btn-archives-back').addEventListener('click', () => showOverlay('menu'));
  byId('btn-evolution-back').addEventListener('click', () => showOverlay('menu'));
  byId('btn-stats-back').addEventListener('click', () => showOverlay('menu'));
  byId('btn-reroll').addEventListener('click', () => {
    pendingSeed = Math.floor(Math.random() * 2 ** 31) >>> 0;
    byId<HTMLInputElement>('set-seed').value = ''; // let the freshly-rolled seed win
    byId('mm-new-preview').innerHTML = newColonyPreviewHTML(currentSeed());
  });
  byId('btn-settings-back').addEventListener('click', () => showOverlay('menu'));
  byId('btn-settings-save').addEventListener('click', saveSettingsFromInputs);
  byId('btn-onboard-start').addEventListener('click', () => { showOverlay(null); resyncTiming(); });

  // Game-over: found a new colony (existing flow), or dismiss to keep spectating.
  byId('btn-gameover-new').addEventListener('click', newColony);
  byId('btn-gameover-watch').addEventListener('click', () => { showOverlay(null); resyncTiming(); });

  byId('btn-wipe').addEventListener('click', () => {
    if (!window.confirm('Delete the saved colony from this browser? This cannot be undone.')) return;
    clearSave();
    dropCurrent(profile); // forget the live-colony snapshot (keep archives + lifetime stats)
    if (!session) refreshMenu();
    showToast('Saved colony deleted.');
  });

  byId('btn-pause').addEventListener('click', togglePause);
  byId('btn-save').addEventListener('click', () => doSave(false));
  byId('btn-menu').addEventListener('click', openMenu);

  byId<HTMLInputElement>('set-tps').addEventListener('input', (e) => {
    byId('set-tps-val').textContent = (e.target as HTMLInputElement).value;
  });

  // Sound volume slider: live preview of level + a sample tick as you drag.
  byId<HTMLInputElement>('set-volume').addEventListener('input', (e) => {
    const pct = Number.parseInt((e.target as HTMLInputElement).value, 10) || 0;
    byId('set-volume-val').textContent = `${pct}%`;
    audio.applySettings(pct / 100, byId<HTMLInputElement>('set-muted').checked);
  });
  byId<HTMLInputElement>('set-volume').addEventListener('change', () => audio.play('click'));
  byId<HTMLInputElement>('set-muted').addEventListener('change', (e) => {
    audio.applySettings(settings.masterVolume, (e.target as HTMLInputElement).checked);
  });

  // Audio is gesture-gated (browsers block it until a user interacts). Init the
  // engine on the first pointer/key event, then apply the saved volume/mute.
  const startAudio = (): void => {
    audio.init();
    audio.resume();
    audio.applySettings(settings.masterVolume, settings.muted);
  };
  window.addEventListener('pointerdown', startAudio, { once: true });
  window.addEventListener('keydown', startAudio, { once: true });

  for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>('.speed'))) {
    b.addEventListener('click', () => {
      // Speed tiers include a fractional 0.5× (observation) — parse as float and
      // clamp to a known SPEED_TIERS value, else fall back to 1×.
      settings.speedMultiplier = clampSpeed(Number.parseFloat(b.dataset['speed'] ?? '1'));
      writeSettings(settings);
      syncSpeedButtons();
    });
  }
  syncSpeedButtons();

  // Region projects (event delegation): start a long-term build.
  byId('projects').addEventListener('click', (e) => {
    if (!session) return;
    const t = e.target as HTMLElement;
    if (!t.classList.contains('proj-start')) return;
    const project = Number.parseInt(t.getAttribute('data-project') ?? '-1', 10);
    if (project < 0) return;
    const r = session.startProject(project);
    if (r.ok) { audio.play('objective'); doSave(true); }
    showToast(r.message);
    updateAll(session);
  });

  // Gene actions (event delegation): apply + ascend.
  byId('genes').addEventListener('click', (e) => {
    if (!session) return;
    const t = e.target as HTMLElement;
    if (t.classList.contains('apply-gene')) {
      const geneId = Number.parseInt(t.getAttribute('data-gene-id') ?? '-1', 10);
      if (geneId < 0) return;
      const caste = Number.parseInt(byId<HTMLSelectElement>('gene-caste').value, 10);
      const restamp = byId<HTMLInputElement>('gene-restamp').checked;
      // Read the gene's body-part type BEFORE applying (the id is still valid here) so we
      // can give a non-blocking off-caste guidance hint. Classification only — it does
      // NOT change whether/how the gene applies (src/sim/casteGenes.ts; Task 4 owns any
      // hard restriction).
      const inv = session.world.genes;
      const geneType = inv.isAlive(geneId) ? inv.typeOf(geneId) : -1;
      const offCaste = geneType >= 0 && !validCastesForType(geneType).includes(caste);
      const r = session.applyGene(geneId, caste as Caste, restamp);
      if (r.ok) { audio.play('geneApply'); checkColonyRecords(session); }
      if (r.ok && offCaste) {
        const part = GENE_TYPE_NAME[geneType] ?? 'This gene';
        const where = CASTE_LABEL[caste] ?? `caste ${caste}`;
        showToast(`${part} genes suit ${casteCompatLabel(geneType)} — applying to ${where}s is off-caste.`, 3600);
      } else {
        showToast(r.message);
      }
      updateAll(session);
    } else if (t.classList.contains('ascend-gene')) {
      const serial = Number.parseInt(t.getAttribute('data-serial') ?? '-1', 10);
      if (serial < 0) return;
      const res = session.ascendGene(serial);
      if (res.ok) audio.play('ascend');
      showToast(res.message);
      if (res.ok) history?.recordAscension(session, res.message);
      updateAll(session);
    }
  });
  // Fusion selection (checkboxes) — UI owns the set; just re-render the list.
  byId('genes').addEventListener('change', (e) => {
    const t = e.target as HTMLElement;
    if (!session || !t.classList.contains('fuse-pick')) return;
    const serial = Number.parseInt(t.getAttribute('data-serial') ?? '-1', 10);
    if (serial >= 0) { toggleFuse(serial); updateGenes(session); }
  });
  byId('btn-fuse').addEventListener('click', () => {
    if (!session) return;
    const sel = fuseSerials();
    if (sel.length !== 3) return;
    const res = session.fuseGenes(sel);
    if (res.ok) audio.play('fusion');
    showToast(res.message);
    if (res.ok) { history?.recordFusion(session, res.message); recordFusion(profile, sel.length); }
    clearFuse();
    updateAll(session);
  });
  // Caste equipment: unlock-slot buttons (event delegation). Spends Mutation Energy,
  // raises the caste's slot cap, records a replay-safe action, then persists.
  byId('equipment').addEventListener('click', (e) => {
    if (!session) return;
    const t = e.target as HTMLElement;
    if (!t.classList.contains('eq-unlock')) return;
    const caste = Number.parseInt(t.getAttribute('data-caste') ?? '-1', 10);
    if (caste < 0) return;
    const r = session.unlockSlotAction(caste);
    if (r.ok) { audio.play('objective'); doSave(true); }
    showToast(r.message);
    updateAll(session);
  });

  // Progression tree overlay.
  byId('btn-tree').addEventListener('click', () => { if (session) { renderTree(session); showOverlay('tree'); } });
  byId('btn-tree-back').addEventListener('click', () => showOverlay(null));
  byId('btn-armory').addEventListener('click', openArmory);
  byId('btn-armory-back').addEventListener('click', () => showOverlay(null));
  byId('btn-inventory').addEventListener('click', openInventory);
  byId('btn-inventory-back').addEventListener('click', () => showOverlay(null));
  // Bottom-center quick-dock — same handlers as the panel buttons, just always-visible.
  byId('dock-inventory').addEventListener('click', openInventory);
  byId('dock-armory').addEventListener('click', openArmory);
  byId('dock-tree').addEventListener('click', () => { if (session) { renderTree(session); showOverlay('tree'); } });
  wireInventory();
  byId('btn-history').addEventListener('click', () => { if (history) { renderHistory(history); showOverlay('history'); } });
  byId('btn-history-back').addEventListener('click', () => showOverlay(null));
  // Wildlife Encyclopedia (two entry points: topbar + right-panel inline button).
  const openEncyclopedia = (): void => { if (session) { renderEncyclopedia(session); showOverlay('encyclopedia'); } };
  byId('btn-encyclopedia').addEventListener('click', openEncyclopedia);
  byId('btn-encyclopedia-2').addEventListener('click', openEncyclopedia);
  byId('btn-encyclopedia-back').addEventListener('click', () => showOverlay(null));
  byId('hist-filters').addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.classList.contains('hist-filter')) setHistoryFilter(t.dataset['type'] ?? 'all');
  });

  // Watch-mode controls.
  byId('btn-camera').addEventListener('click', () => { watch?.cycleMode(); updateWatchControls(); });
  byId('btn-observe').addEventListener('click', () => { watch?.cycleObserved(); updateWatchControls(); });
  byId('btn-watch').addEventListener('click', toggleWatchOverlay);

  // On-screen camera controls (the touch/click path; canvas drag+wheel cover mouse).
  byId('btn-zoom-in').addEventListener('click', () => zoomCenter(BTN_ZOOM_STEP));
  byId('btn-zoom-out').addEventListener('click', () => zoomCenter(1 / BTN_ZOOM_STEP));
  byId('btn-recenter').addEventListener('click', recenterOnNest);

  // Generic UI click cue: a soft tick on any button press, EXCEPT buttons that play
  // their own richer cue (box open, gene apply/ascend/fuse) so cues never double up.
  const RICHER_CUE_IDS = new Set(['btn-open-box', 'btn-fuse']);
  document.addEventListener('pointerdown', (e) => {
    const b = (e.target as HTMLElement).closest('button');
    if (!b || RICHER_CUE_IDS.has(b.id)) return;
    if (b.classList.contains('apply-gene') || b.classList.contains('ascend-gene')) return;
    audio.play('click');
  }, true);

  // Evolution box: open, skip-to-reveal, and collect.
  byId('btn-open-box').addEventListener('click', openBoxFlow);
  byId('btn-box-collect').addEventListener('click', finishBox);
  // Manual reward resolution — single click per gene (EQUIP / SACRIFICE / SAVE).
  byId('box-resolve').addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('[data-act]');
    if (!btn) return;
    e.stopPropagation();
    resolvePending(btn.getAttribute('data-act') ?? 'save');
  });
  byId('box-stage').addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('#btn-box-collect') || t.closest('#box-resolve')) return;
    if (pendingResolve.length > 0) return; // resolving — a stray click must not skip/close
    if (byId('box-footer').hidden) skipBoxAnimation();
    else finishBox();
  });

  // Auto-Evolution rules overlay.
  byId('btn-autorules').addEventListener('click', openAutoRules);
  byId('btn-autorules-back').addEventListener('click', () => showOverlay(null));
  byId('autorules-body').addEventListener('change', readAutoRulesForm);
  byId('autorules-body').addEventListener('input', readAutoRulesForm);
}

/** Build + show the Colony Evolution Armory from the applyGene log + power snapshot. */
/** Applied genes reconstructed from the player's applyGene log (the equipped set). */
function appliedGenes(): AppliedGene[] {
  const out: AppliedGene[] = [];
  if (!session) return out;
  for (const a of session.actions) {
    if (a.kind === 'applyGene') out.push({ caste: a.caste, type: a.baseType, rarity: a.rarity, traits: a.traits, name: a.name });
  }
  return out;
}

function openArmory(): void {
  if (!session) return;
  byId('armory-body').innerHTML =
    wishlistStripHTML(wishlistProgress(wishlist.targets, session.geneRows())) +
    armoryHTML(buildArmory(appliedGenes(), effectiveSnapshot(session.powerSnapshot())), CASTE_NAME);
  showOverlay('armory');
}

// --- Gene Inventory (the first-class loot-management screen) ----------------

function invOpts(): InventoryOpts {
  return { caste: invCaste, sort: invSort, query: invQuery, filters: invFilters, wishlist: targetKeySet(wishlist) };
}

function openInventory(): void {
  if (!session) return;
  renderInventoryBody();
  showOverlay('inventory');
}

/** Re-render the whole body (Wishlist section + toolbar + grid) — for tab / filter /
 *  wishlist changes. The Wishlist progress sits above the loot grid so the player's
 *  personal goals frame the screen. */
function renderInventoryBody(): void {
  if (!session) return;
  const rows = session.geneRows();
  const view = buildInventory(rows, appliedGenes(), invOpts());
  byId('inventory-body').innerHTML =
    wishlistSectionHTML(wishlistProgress(wishlist.targets, rows)) +
    inventoryBodyHTML(invOpts(), view, CASTE_NAME, session.playerGeneration());
}

/** Persist the wishlist + refresh the inventory so the section progress and the card
 *  ☆ stars reflect the change. (Render-only — the wishlist is never sim/save state.) */
function commitWishlist(): void {
  writeWishlist(wishlist);
  renderInventoryBody();
}

/** Re-render ONLY the results grid — keeps the search box focused while typing/sorting. */
function renderInventoryGrid(): void {
  if (!session) return;
  const view = buildInventory(session.geneRows(), appliedGenes(), invOpts());
  const grid = document.getElementById('inv-grid');
  if (grid) grid.innerHTML = inventoryGridHTML(view, CASTE_NAME, session.playerGeneration());
}

/** Wire the inventory's delegated controls ONCE (the body's innerHTML re-renders under it). */
function wireInventory(): void {
  const body = byId('inventory-body');
  body.addEventListener('click', (e) => {
    if (!session) return;
    const t = e.target as HTMLElement;
    // --- Evolution Wishlist controls (the ☆ toggles + the section's add/remove) -----
    const wishToggle = t.closest('[data-wish-toggle]');
    if (wishToggle) {
      const p = parseTargetKey(wishToggle.getAttribute('data-wish-toggle') ?? '');
      if (p) {
        const now = toggleTarget(wishlist, p.type, p.rarity);
        audio.play('click');
        showToast(now
          ? `★ ${wishName(p.type, p.rarity)} added to your wishlist.`
          : `Removed ${wishName(p.type, p.rarity)} from your wishlist.`);
        commitWishlist();
      }
      return;
    }
    const wishRemove = t.closest('[data-wish-remove]');
    if (wishRemove) {
      const p = parseTargetKey(wishRemove.getAttribute('data-wish-remove') ?? '');
      if (p && removeTarget(wishlist, p.type, p.rarity)) { audio.play('click'); commitWishlist(); }
      return;
    }
    if (t.closest('[data-wish-add]')) {
      const typeSel = document.getElementById('wish-type') as HTMLSelectElement | null;
      const raritySel = document.getElementById('wish-rarity') as HTMLSelectElement | null;
      if (typeSel && raritySel) {
        const type = Number.parseInt(typeSel.value, 10);
        const rarity = Number.parseInt(raritySel.value, 10);
        const ok = addTarget(wishlist, type, rarity);
        audio.play('click');
        showToast(ok
          ? `★ ${wishName(type, rarity)} added to your wishlist.`
          : `Already wished for, or the wishlist is full (max ${TARGET_CAP}).`);
        if (ok) commitWishlist();
      }
      return;
    }
    const tab = t.closest('[data-inv-caste]');
    if (tab) { invCaste = Number.parseInt(tab.getAttribute('data-inv-caste') ?? '-1', 10); renderInventoryBody(); return; }
    const chip = t.closest('[data-inv-filter]');
    if (chip) {
      const f = chip.getAttribute('data-inv-filter') as InvFilter;
      invFilters = invFilters.includes(f) ? invFilters.filter((x) => x !== f) : [...invFilters, f];
      renderInventoryBody(); return;
    }
    const apply = t.closest('[data-inv-apply]');
    if (apply) {
      const id = Number.parseInt(apply.getAttribute('data-inv-apply') ?? '-1', 10);
      const type = Number.parseInt(apply.getAttribute('data-inv-type') ?? '-1', 10);
      if (id < 0 || type < 0) return;
      const r = session.applyGene(id, primaryCasteForType(type) as Caste, false);
      if (r.ok) { audio.play('geneApply'); checkColonyRecords(session); }
      showToast(r.message);
      updateAll(session); renderInventoryBody(); doSave(true);
      return;
    }
    const asc = t.closest('[data-inv-ascend]');
    if (asc) {
      const serial = Number.parseInt(asc.getAttribute('data-inv-ascend') ?? '-1', 10);
      if (serial < 0) return;
      const r = session.ascendGene(serial);
      if (r.ok) { audio.play('ascend'); history?.recordAscension(session, r.message); }
      showToast(r.message);
      updateAll(session); renderInventoryBody(); doSave(true);
      return;
    }
    const sac = t.closest('[data-inv-sacrifice]');
    if (sac) {
      const serial = Number.parseInt(sac.getAttribute('data-inv-sacrifice') ?? '-1', 10);
      if (serial < 0) return;
      const r = session.sacrificeGene(serial); // same replay-safe action the box reveal uses
      if (r.energy > 0) { audio.play('click'); recordSacrifice(profile, 1); }
      showToast(r.energy > 0 ? `Sacrificed for +${r.energy} Mutation Energy.` : 'That gene could not be sacrificed.');
      updateAll(session); renderInventoryBody(); doSave(true);
      return;
    }
  });
  body.addEventListener('input', (e) => {
    if ((e.target as HTMLElement).id === 'inv-search') { invQuery = (e.target as HTMLInputElement).value; renderInventoryGrid(); }
  });
  body.addEventListener('change', (e) => {
    if ((e.target as HTMLElement).id === 'inv-sort') { invSort = (e.target as HTMLSelectElement).value as InvSort; renderInventoryGrid(); }
  });
}

function openAutoRules(): void {
  byId('autorules-body').innerHTML =
    autoRulesFormHTML(autoRules) + '<div id="ar-estimate" class="ar-estimate"></div>';
  renderAutoEstimate();
  showOverlay('autorules');
}

/** Read the auto-rules form back into state + persist + refresh the live estimate. */
function readAutoRulesForm(): void {
  const cb = (id: string): boolean => byId<HTMLInputElement>(id).checked;
  autoRules = {
    enabled: cb('ar-enabled'),
    equipUpgrades: cb('ar-equip'),
    sacrificeDuplicates: cb('ar-sac-dup'),
    saveLegendaries: cb('ar-save-leg'),
    saveApex: cb('ar-save-apex'),
    sacrificeCommons: cb('ar-sac-com'),
    sacrificeBelowPower: cb('ar-sac-pow'),
    belowPowerX: clampInt(Number.parseInt(byId<HTMLInputElement>('ar-pow').value, 10), 0, 9999, autoRules.belowPowerX),
    keepRest: cb('ar-keep-rest'),
  };
  saveAutoRules(autoRules);
  renderAutoEstimate();
}

/** Live preview: apply the current rules to the player's whole inventory. */
function renderAutoEstimate(): void {
  const el = document.getElementById('ar-estimate');
  if (!el) return;
  if (!session) { el.textContent = ''; return; }
  const infos: RewardInfo[] = [];
  const genes = session.playerGenes();
  for (const pg of genes) {
    const gi = session.geneInfo(pg.id);
    if (!gi) continue;
    let bestPower = -1;
    for (const og of genes) {
      if (og.id === pg.id) continue;
      const o = session.geneInfo(og.id);
      if (!o || o.type !== gi.type) continue;
      if (o.power > bestPower) bestPower = o.power;
    }
    infos.push({
      type: gi.type, rarity: gi.rarity, traitTotal: gi.traitTotal, power: gi.power,
      isDuplicate: bestPower >= 0, isUpgrade: bestPower >= 0 ? gi.power > bestPower : true,
    });
  }
  const s = summarize(infos, autoRules);
  el.innerHTML = `<b>On your current ${infos.length} gene${infos.length === 1 ? '' : 's'}:</b> ` +
    `⚡ equip ${s.equip} · ♻ sacrifice ${s.sacrifice} <span class="mm-accent">(~${s.energy} energy)</span> ` +
    `· 💾 save ${s.save} · ❓ ask ${s.ask}`;
}

function clampInt(v: number, lo: number, hi: number, fallback: number): number {
  if (!Number.isFinite(v)) return fallback;
  return Math.min(hi, Math.max(lo, Math.trunc(v)));
}

/** Valid speed multipliers (the SPEED_TIERS values), ascending, for round-tripping. */
const SPEED_VALUES: readonly number[] = Object.values(SPEED_TIERS).slice().sort((a, b) => a - b);

/**
 * Clamp a speed multiplier to a known SPEED_TIERS value (e.g. the persisted 0.5×
 * observation tier), falling back to 1× for anything unrecognised. Keeps the
 * settings round-trip valid against the canonical tier set.
 */
function clampSpeed(v: number): number {
  return SPEED_VALUES.includes(v) ? v : 1;
}

boot();
